import { Mail, MapPin, CalendarDays } from "lucide-react";
import { useSeo } from "@/lib/site";
import { CALENDLY_URL, CONTACT_EMAIL } from "@/data/nav";
import { OFFICES } from "@/components/site/Footer";
import { MaskedLines, FadeIn, Reveal } from "@/components/site/Motion";
import { BookMeeting, CtaButton, CtaRow, Label } from "@/components/site/Blocks";

export default function Contact() {
  useSeo("Contact Us | Book a Meeting | Perfect Digitals", "Contact Perfect Digitals, book a meeting with our SEO and AI search team, or visit our London and Dublin offices.");
  return (
    <div data-testid="contact-page">
      <section className="grain relative overflow-hidden bg-ink pb-20 pt-36 text-white sm:pb-28 sm:pt-44">
        <div aria-hidden="true" className="grid-lines absolute inset-0 opacity-60 [mask-image:radial-gradient(ellipse_at_top_right,black,transparent_70%)]" />
        <div aria-hidden="true" className="absolute right-[-10%] top-[-20%] h-[600px] w-[600px] rounded-full bg-violet-700/25 blur-[140px]" />
        <div className="wrap relative grid grid-cols-1 gap-14 lg:grid-cols-12 lg:items-end">
          <div className="min-w-0 lg:col-span-7">
            <FadeIn className="font-mono text-[11px] uppercase tracking-[0.25em] text-violet-300">Contact</FadeIn>
            <MaskedLines testid="contact-title" lines={["Let's Talk", <span className="font-serif-i text-gradient">Search Visibility.</span>]} className="mt-6 font-display text-[2.8rem] font-medium leading-[0.95] sm:text-7xl lg:text-[5.4rem]" />
            <FadeIn delay={0.5}>
              <p className="mt-8 max-w-xl text-[15px] leading-relaxed text-slate-300 sm:text-base">Book a meeting with our team or email us to talk about your visibility across Google and AI-powered search.</p>
              <CtaRow className="mt-10">
                <BookMeeting testid="contact-book-meeting" />
                <CtaButton label="Email Us" href={`mailto:${CONTACT_EMAIL}`} variant="ghost" testid="contact-email-button" />
              </CtaRow>
            </FadeIn>
          </div>
          <FadeIn delay={0.7} className="grid min-w-0 gap-3 lg:col-span-5">
            <a href={`mailto:${CONTACT_EMAIL}`} data-testid="contact-email-card" className="group flex min-w-0 items-center gap-5 rounded-3xl border border-white/10 bg-white/[0.04] p-6 transition-colors hover:border-violet-400">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-violet-500/20 text-violet-200"><Mail className="h-5 w-5" /></span>
              <span className="min-w-0"><span className="block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">Email</span><span className="block break-all font-display text-[15px] sm:text-xl">{CONTACT_EMAIL}</span></span>
            </a>
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" data-testid="contact-calendly-card" className="group flex min-w-0 items-center gap-5 rounded-3xl border border-white/10 bg-white/[0.04] p-6 transition-colors hover:border-violet-400">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-violet-500/20 text-violet-200"><CalendarDays className="h-5 w-5" /></span>
              <span><span className="block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">Book a meeting</span><span className="block font-display text-lg sm:text-xl">30-minute call with our team ↗</span></span>
            </a>
          </FadeIn>
        </div>
      </section>

      <section className="bg-[#F3F2F8] py-20 sm:py-28" data-testid="contact-calendar-section">
        <div className="wrap">
          <Reveal><Label n="01">Book a meeting</Label></Reveal>
          <Reveal delay={0.05}><h2 className="mt-6 font-display text-[2.1rem] font-medium leading-[1.02] text-slate-900 sm:text-5xl">Pick a time that suits you</h2></Reveal>
          <Reveal delay={0.1} className="mt-10 overflow-hidden rounded-[28px] border border-slate-200 bg-white">
            <iframe title="Book a meeting with Perfect Digitals" data-testid="calendly-embed" src={`${CALENDLY_URL}?hide_gdpr_banner=1&primary_color=6d28d9`} className="h-[720px] w-full" loading="lazy" />
          </Reveal>
        </div>
      </section>

      <section className="bg-[#FAFAFC] py-20 sm:py-28" data-testid="contact-offices-section">
        <div className="wrap">
          <Reveal><Label n="02">Offices</Label></Reveal>
          <Reveal delay={0.05}><h2 className="mt-6 font-display text-[2.1rem] font-medium leading-[1.02] text-slate-900 sm:text-5xl">Our offices</h2></Reveal>
          <div className="mt-12 grid gap-4 md:grid-cols-2">
            {OFFICES.map((o, i) => (
              <Reveal key={o.label} delay={i * 0.08} className="glow-card rounded-3xl border border-slate-200 bg-white p-8 sm:p-10" data-testid={`contact-office-${i}`}>
                <MapPin className="h-6 w-6 text-violet-700" />
                <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.2em] text-violet-700">{o.label}</p>
                <address className="mt-3 font-display text-2xl not-italic leading-snug text-slate-900 sm:text-3xl">
                  {o.lines.map((l) => <span key={l} className="block">{l}</span>)}
                </address>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
